import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import LoadingAnimation from './LoadingAnimation';
import SimbyAnimation from './SimbyAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert YouTube URL to embed format
const convertYouTubeUrl = (url) => {
  if (!url) return '';

  // Already in embed format
  if (url.includes('/embed/')) return url;

  // Extract video ID from various YouTube URL formats
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}`;
  }

  return url; // Return original if can't parse
};

const VideoLesson = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [completed, setCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nextLesson, setNextLesson] = useState(null);
  const [islandId, setIslandId] = useState(1);
  const [resourcesEarned, setResourcesEarned] = useState(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [celebrationPotId, setCelebrationPotId] = useState(null);
  const [gamificationEnabled, setGamificationEnabled] = useState(false); // flag Configurações
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportDescription, setReportDescription] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then(r => setGamificationEnabled(!!r.data?.gamification_enabled))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchModule = async () => {
      try {
        const response = await axios.get(`${API}/modules/${lessonId}`, { withCredentials: true });
        const mod = response.data;
        setLesson(mod);
        // Fetch parent lesson to get island_id
        if (mod.lesson_id) {
          try {
            const lessonRes = await axios.get(`${API}/lessons/${mod.lesson_id}`, { withCredentials: true });
            setIslandId(lessonRes.data.island_id || 1);
          } catch { /* keep default */ }
        }
      } catch (error) {
        console.error('Error fetching module:', error);
        setLesson(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchModule();
  }, [lessonId]);

  // Fetch next module when video is completed
  const fetchNextLesson = async () => {
    if (!lesson) return;

    try {
      const allModulesRes = await axios.get(
        `${API}/modules?lesson_id=${lesson.lesson_id}`,
        { withCredentials: true }
      );
      const lessonModules = allModulesRes.data.sort((a, b) => (a.order || 0) - (b.order || 0));
      const currentIndex = lessonModules.findIndex(m => m.module_id === lessonId);

      if (currentIndex !== -1 && currentIndex < lessonModules.length - 1) {
        setNextLesson(lessonModules[currentIndex + 1]);
      } else {
        setNextLesson({ isLastModule: true, lesson_id: lesson.lesson_id });
      }
    } catch (error) {
      console.error('Error fetching next module:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Carregando lição...</p>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-xl text-gray-600">Lição não encontrada</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Voltar ao Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const handleComplete = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setCompleted(true);

    try {
      const completionRes = await axios.post(
        `${API}/auth/complete-lesson`,
        {
          lesson_id: lessonId,
          action_type: 'video',
          island_id: islandId,
          score: 100
        },
        { withCredentials: true }
      );

      if (completionRes.data?.message === "Lesson already completed") {
        setAlreadyDone(true);
      } else if (completionRes.data?.resources_earned) {
        setResourcesEarned(completionRes.data.resources_earned);
        setPointsEarned(completionRes.data.points_earned ?? 0);
      }
      if (completionRes.data?.simby_celebration && completionRes.data?.pot?.pot_id) {
        setCelebrationPotId(completionRes.data.pot.pot_id);
      }

      // Refresh user data
      await checkAuth();

      // Fetch next lesson after completion
      await fetchNextLesson();
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendReport = async () => {
    if (isSendingReport) return;
    setIsSendingReport(true);
    try {
      await axios.post(
        `${API}/admin/report-exercise-error`,
        {
          module_id: lessonId,
          question_text: lesson?.title || '',
          question_type: 'video',
          error_description: reportDescription,
        },
        { withCredentials: true }
      );
      setReportSent(true);
      setShowReportForm(false);
    } catch (e) {
      console.error('Error sending report:', e);
    } finally {
      setIsSendingReport(false);
    }
  };

  const navigateToNextLesson = () => {
    if (!nextLesson) return;
    if (nextLesson.isLastModule) {
      navigate(nextLesson.lesson_id ? `/lesson-modules/${nextLesson.lesson_id}` : '/dashboard');
      return;
    }
    const nextType = nextLesson.type === 'quiz' ? 'quiz' :
      nextLesson.type === 'video' ? 'lesson' : 'simulator';
    navigate(`/${nextType}/${nextLesson.module_id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => lesson?.lesson_id ? navigate(`/lesson-modules/${lesson.lesson_id}`) : navigate('/dashboard')}
            variant="ghost"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{lesson.video_title || lesson.title}</h1>
          {(lesson.video_description || lesson.description) && (
            <p className="text-gray-600 whitespace-pre-line">{lesson.video_description || lesson.description}</p>
          )}
        </div>

        {/* Video Player */}
        <Card className="mb-6 overflow-hidden">
          <div className="aspect-video bg-gray-900">
            <iframe
              width="100%"
              height="100%"
              src={convertYouTubeUrl(lesson.video_url || lesson.videoUrl)}
              title={lesson.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </Card>

        {/* Lesson Info — compact strip */}
        <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
          {lesson.duration && <span>⏱️ {lesson.duration}</span>}
          <span>📚 Vídeo · 🪣🪣🪣 +1pt</span>
        </div>

        {/* Complete Button / Completion Card */}
        {!completed ? (
          <Button
            onClick={handleComplete}
            size="lg"
            className="w-full bg-purple-600 hover:bg-purple-700 py-4 text-lg"
            disabled={isSaving}
          >
            <CheckCircle2 className="w-6 h-6 mr-3" />
            {isSaving ? 'Salvando...' : 'Marcar como Concluído'}
          </Button>
        ) : (
          <div>
            {/* Resources earned banner */}
            {alreadyDone && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-3 text-center">
                <p className="text-sm text-gray-500">✅ Você já havia concluído este vídeo anteriormente.</p>
              </div>
            )}
            {gamificationEnabled && resourcesEarned && !alreadyDone && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3 text-center">
                <p className="text-sm font-semibold text-purple-700 mb-1">🎉 Recursos ganhos!</p>
                <div className="flex justify-center gap-4 text-sm text-gray-700">
                  {resourcesEarned.watering_cans > 0 && <span>🪣 +{resourcesEarned.watering_cans} Regadores</span>}
                  {resourcesEarned.waterings > 0 && <span>💧 +{resourcesEarned.waterings} Regas</span>}
                  {resourcesEarned.fertilizers > 0 && <span>🌿 +{resourcesEarned.fertilizers} Adubos</span>}
                  {pointsEarned > 0 && <span>🌱 +{pointsEarned} pt na acácia</span>}
                </div>
              </div>
            )}
            {nextLesson ? (
              <Button
                onClick={navigateToNextLesson}
                size="lg"
                className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg mb-3"
              >
                {nextLesson.isLastModule ? 'Voltar à Lição' : `Próxima Atividade: ${nextLesson.title}`}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/dashboard')}
                size="lg"
                className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg mb-3"
              >
                Voltar ao Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        )}

        {/* Report problem */}
        <div className="mt-4">
          {!reportSent && !showReportForm && (
            <button
              className="text-xs text-yellow-700 underline-offset-2 hover:underline flex items-center gap-1 opacity-70 hover:opacity-100"
              onClick={() => setShowReportForm(true)}
            >
              <AlertTriangle className="w-3 h-3" />
              Reportar problema neste vídeo
            </button>
          )}
          {showReportForm && (
            <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-xl">
              <p className="text-sm font-semibold text-yellow-800 mb-1">O que você viu?</p>
              <textarea
                className="w-full border border-yellow-300 rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-yellow-400"
                rows={3}
                placeholder="Vídeo indisponível, áudio com problema, conteúdo incorreto..."
                value={reportDescription}
                onChange={e => setReportDescription(e.target.value)}
              />
              <div className="flex gap-2 mt-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setShowReportForm(false); setReportDescription(''); }}>Cancelar</Button>
                <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white" onClick={handleSendReport} disabled={isSendingReport}>
                  {isSendingReport ? 'Enviando...' : 'Enviar'}
                </Button>
              </div>
            </div>
          )}
          {reportSent && (
            <p className="text-xs text-yellow-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Obrigada — vamos verificar isso.
            </p>
          )}
        </div>

        {/* Simby celebration */}
        {gamificationEnabled && celebrationPotId && (
          <SimbyAnimation potId={celebrationPotId} onDone={() => setCelebrationPotId(null)} />
        )}

        {/* Video Description */}
        {!completed && (lesson.video_description || lesson.videoDescription) && (
          <Card className="mt-6 p-6">
            <p className="text-gray-700 whitespace-pre-line">
              {lesson.video_description || lesson.videoDescription}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default VideoLesson;

